import React, { useMemo, useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { useQueries } from '@tanstack/react-query';
import {
  useTable,
  appTableFeatures,
  flexRender,
  type ColumnDef,
} from '@/lib/tanstack-table';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CheckIcon,
  PlusIcon,
  PackageIcon,
  DotsThreeIcon,
  WarningIcon,
  InfoIcon,
  ShoppingCartIcon,
  TruckIcon,
  CurrencyDollarIcon,
  GitDiffIcon,
  XIcon,
  PencilSimpleIcon,
  CircleNotchIcon,
  FileTextIcon,
  ArrowSquareOutIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { orpc, rpcClient } from '@/api/rpc/client';
import {
  useEntityAssociationsList,
  useCreateEntityAssociation,
  useDeleteEntityAssociation,
  useConfirmEntityAssociationMatch,
} from '@/api/rpc/hooks/core/useEntityAssociations';
import {
  SystemEntity,
  EntityAssociationType,
  type ChargeableEntity,
} from '@erp/shared-types';
import {
  isTentativeMatch,
  isManualMatch,
  getActiveHoldPay,
  getActiveConsign,
  getMatchTrouble,
} from '@/components/accounts-payable/shared/match-display-state';
import { TroubleDataBadges } from '@/components/common/TroubleDataBadges';
import { SystemEntityInline } from '@/components/system-entities';
import { usePurchaseOrder } from '@/api/rpc/hooks/accounts-payable/usePurchaseOrders';
import { useSalesOrder } from '@/api/rpc/hooks/accounts-receivable/useSalesOrders';
import { useChargesForEntity } from '@/api/rpc/hooks/finance/useCharges';
import { useUpdateAPInvoiceLineItem } from '@/api/rpc/hooks/accounts-payable/useUpdateAPInvoiceLineItem';
import { useAPInvoiceSupportingDocuments } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import { SplitLineItemButton } from './SplitLineItemDialog';
import { AggregateLinesBar, LineSelectCheckbox } from './AggregateLinesBar';
import { useLineSelection } from './useLineSelection';
import { useLineItemAssociationsMap } from './useLineItemAssociationsMap';
import { SplitGroupSubtotalRow, SplitMemberMarker } from './SplitLineGroupRows';
import { buildSplitGroups, splitMemberRowClass } from './splitLineGroups';
import {
  confirmedSplitParts,
  confirmedSplitTotal,
  type ConfirmedSplitPart,
} from './confirmedSplitMatch';
import { MatchCellOrSplitSuggestion } from './SplitSuggestionMarker';
import { getClassificationLabel } from '@/utils/emailClassificationUtils';
import { useCan } from '@/context/authz';
import { useToast } from '@/context/ToastContext';
import { Action, Subject } from '@erp/authz';
import type {
  PurchaseOrderLineItem,
  SalesOrderLineItem,
  Charge,
  EntityAssociationItem,
  SupportingDocItem,
  APInvoiceLineItemSplit,
  APInvoiceLineItemSplitProposal,
} from '@erp/rpc';

interface InvoiceLineItemDisplay {
  id: string;
  lineNumber: number;
  description?: string | null;
  quantity?: string | number | null;
  unitPrice?: string | null;
  totalPrice?: string | null;
  split?: APInvoiceLineItemSplit | null;
  splitProposal?: APInvoiceLineItemSplitProposal | null;
}

interface InvoiceLineItemsProps {
  invoiceId: string;
  lineItems: InvoiceLineItemDisplay[];
  currency?: string;
  showMatching?: boolean;
  canEditMatches?: boolean;
  /** The invoice's supplier; keeps another vendor's order lines out of the aggregate picker. */
  supplierId?: string | null;
}

/**
 * Info about a single aggregation group (several invoice lines rolled up to
 * one SO/PO/TO line or charge). Derived from `metadata.aggregationGroupId`
 * on the line item associations.
 */
interface AggregationGroupInfo {
  groupId: string;
  memberIds: string[];
  memberIndexById: Map<string, number>;
  summedQuantity: number;
  summedTotal: number;
  targetEntityType: SystemEntity | null;
  targetEntityId: string | null;
  matchMetadata: EntityAssociationItem['metadata'] | null;
  /** Canonical group description from the agent (e.g. "Mango"). */
  commonDescription: string | null;
}

interface AggregationMaps {
  lineIdToGroupId: Map<string, string>;
  groups: Map<string, AggregationGroupInfo>;
}

function readAggregationGroupId(
  associations: EntityAssociationItem[] | undefined,
): { groupId: string; association: EntityAssociationItem } | null {
  if (!associations) return null;
  for (const assoc of associations) {
    const groupId = assoc.metadata?.aggregationGroupId;
    if (typeof groupId === 'string' && groupId.length > 0) {
      return { groupId, association: assoc };
    }
  }
  return null;
}

function findAssociatedEntityId(
  associations: EntityAssociationItem[] | undefined,
  entityType: SystemEntity,
): string {
  return (
    associations?.find(
      (association) => association.entity.entityType === entityType,
    )?.entity.entityId ?? ''
  );
}

const EMPTY_ASSOCIATIONS: NonNullable<
  MatchingCellProps['lineItemAssociations']
> = [];

function EditableComparisonCell({
  row,
  invoiceId,
  field,
  updateLineItem,
  canEdit,
  matchedPOLineItem,
  matchedSOLineItem,
  currency,
}: {
  row: InvoiceLineItemDisplay;
  invoiceId: string;
  field: 'quantity' | 'unitPrice';
  updateLineItem: ReturnType<typeof useUpdateAPInvoiceLineItem>;
  canEdit: boolean;
  matchedPOLineItem?: PurchaseOrderLineItem;
  matchedSOLineItem?: SalesOrderLineItem;
  currency?: string;
}) {
  const gt = useGT();
  const [isEditing, setIsEditing] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const value = field === 'quantity' ? row.quantity : row.unitPrice;
  const formattedValue =
    field === 'quantity'
      ? formatQuantity(typeof value === 'number' ? String(value) : value)
      : formatCurrency(
          typeof value === 'number' ? String(value) : value,
          currency,
        );
  const [editValue, setEditValue] = React.useState(
    String(value ?? (field === 'quantity' ? 0 : '0')),
  );

  // Sync editValue with current value when prop changes, but preserve edits in progress
  React.useEffect(() => {
    if (!isEditing) {
      setEditValue(String(value ?? (field === 'quantity' ? 0 : '0')));
    }
  }, [value, field, isEditing]);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    const trimmed = editValue.trim() || '0';
    const payload = { id: row.id, apInvoiceId: invoiceId, [field]: trimmed };
    try {
      await updateLineItem.mutateAsync(payload);
      setIsEditing(false);
    } catch {
      // Error handling done in mutation hook
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(String(value ?? (field === 'quantity' ? 0 : '0')));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (!canEdit) {
    const rows: ComparisonRow[] = [{ label: 'Inv', formattedValue }];
    if (matchedPOLineItem) {
      const poValue =
        field === 'quantity'
          ? matchedPOLineItem.quantity
          : matchedPOLineItem.unitPrice === null ||
              matchedPOLineItem.unitPrice === undefined
            ? null
            : String(matchedPOLineItem.unitPrice);
      const poFormattedValue =
        field === 'quantity'
          ? formatQuantity(
              typeof poValue === 'number' ? String(poValue) : poValue,
            )
          : formatCurrency(
              typeof poValue === 'number' ? String(poValue) : poValue,
              currency,
            );
      rows.push({
        label: 'PO',
        formattedValue: poFormattedValue,
        isDifferent: valuesDiffer(value, poValue),
      });
    } else if (matchedSOLineItem) {
      const soValue =
        field === 'quantity'
          ? getSOComparisonQuantity(matchedSOLineItem)
          : getSOComparisonUnitPrice(matchedSOLineItem);
      const soFormattedValue =
        field === 'quantity'
          ? formatQuantity(
              typeof soValue === 'number' ? String(soValue) : soValue,
            )
          : formatCurrency(
              typeof soValue === 'number' ? String(soValue) : soValue,
              currency,
            );
      // Tooltip when showing finalized supplier qty that differs from SO-stage
      const isQtyField = field === 'quantity';
      const showSupplierQtyTooltip =
        isQtyField &&
        matchedSOLineItem.supplierQuantity != null &&
        matchedSOLineItem.quantity != null &&
        Number(matchedSOLineItem.supplierQuantity) !==
          Number(matchedSOLineItem.quantity);
      rows.push({
        label: 'SO',
        formattedValue: soFormattedValue,
        isDifferent: valuesDiffer(value, soValue),
        tooltip: showSupplierQtyTooltip
          ? gt('Finalized shipped qty — SO-stage: {qty}', {
              qty: formatQuantity(
                typeof matchedSOLineItem.quantity === 'number'
                  ? String(matchedSOLineItem.quantity)
                  : matchedSOLineItem.quantity,
              ),
            })
          : undefined,
      });
    }
    return <ComparisonCell rows={rows} />;
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          step="any"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={updateLineItem.isPending}
          className="h-7 text-sm font-mono w-20 text-right border border-input rounded px-2"
          onClick={(e) => e.stopPropagation()}
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            void handleSave();
          }}
          disabled={updateLineItem.isPending}
          className="h-7 w-7 p-0"
        >
          {updateLineItem.isPending ? (
            <CircleNotchIcon className="h-3 w-3 animate-spin" />
          ) : (
            <CheckIcon className="h-3 w-3 text-green-600" />
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleCancel();
          }}
          disabled={updateLineItem.isPending}
          className="h-7 w-7 p-0"
        >
          <XIcon className="h-3 w-3 text-red-600" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1 group">
        <span className="text-sm font-mono text-right flex-1">
          {formattedValue}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
          className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <PencilSimpleIcon className="h-3 w-3" />
        </Button>
      </div>
      {matchedPOLineItem && (
        <div className="text-xs text-muted-foreground">
          <T>PO:</T>{' '}
          {field === 'quantity'
            ? formatQuantity(
                typeof matchedPOLineItem.quantity === 'number'
                  ? String(matchedPOLineItem.quantity)
                  : matchedPOLineItem.quantity,
              )
            : formatCurrency(
                matchedPOLineItem.unitPrice === null ||
                  matchedPOLineItem.unitPrice === undefined
                  ? null
                  : typeof matchedPOLineItem.unitPrice === 'number'
                    ? String(matchedPOLineItem.unitPrice)
                    : matchedPOLineItem.unitPrice,
                currency,
              )}
        </div>
      )}
      {matchedSOLineItem && (
        <div className="text-xs text-muted-foreground">
          <T>SO:</T>{' '}
          {field === 'quantity'
            ? (() => {
                const soQty = getSOComparisonQuantity(matchedSOLineItem);
                return formatQuantity(
                  typeof soQty === 'number' ? String(soQty) : soQty,
                );
              })()
            : (() => {
                const soPrice = getSOComparisonUnitPrice(matchedSOLineItem);
                return formatCurrency(
                  typeof soPrice === 'number' ? String(soPrice) : soPrice,
                  currency,
                );
              })()}
        </div>
      )}
    </div>
  );
}

interface AssociatedDocument {
  entity: {
    entityId: string;
    entityType: SystemEntity;
  };
  associationType?: string | null;
}

interface MatchingCellProps {
  lineItemId: string;
  lineItem: InvoiceLineItemDisplay;
  canEditMatches: boolean;
  associatedPOs: AssociatedDocument[];
  associatedSOs: AssociatedDocument[];
  associatedShipments: AssociatedDocument[];
  // Pre-fetched associations for this line item to avoid N+1 queries
  lineItemAssociations?: EntityAssociationItem[];
  allMatchedPOLineItemIds: Set<string>;
  allMatchedSOLineItemIds: Set<string>;
  allMatchedChargeIds: Set<string>;
  // Pre-fetched matched PO line item data
  matchedPOLineItem?: PurchaseOrderLineItem;
  // Pre-fetched matched SO line item data
  matchedSOLineItem?: SalesOrderLineItem;
  // Pre-fetched matched charge data
  matchedCharge?: Charge;
  currency?: string;
  // When set, the row is part of an aggregation group — the real match chip
  // lives on the group's subtotal row, so this cell shows a muted marker.
  aggregationInfo?: {
    memberIndex: number;
    memberCount: number;
  };
}

/**
 * Returns the best unit price to use for AP invoice comparison from an SO line item.
 * Priority: supplierCostPrice (finalized supplier cost) > costPrice (SO-stage estimate) > unitPrice (sale price)
 */
function getSOComparisonUnitPrice(
  soLineItem: SalesOrderLineItem,
): string | null | undefined {
  return (
    soLineItem.supplierCostPrice ?? soLineItem.costPrice ?? soLineItem.unitPrice
  );
}

/**
 * Returns the best quantity to use for AP invoice comparison from an SO line item.
 * Priority: supplierQuantity (finalized shipped qty from AR_TRX_LINE.QNT) > quantity (SO-stage estimate).
 */
function getSOComparisonQuantity(
  soLineItem: SalesOrderLineItem,
): number | null | undefined {
  return soLineItem.supplierQuantity ?? soLineItem.quantity;
}

/**
 * Returns the best total to use for AP invoice comparison from an SO line item.
 * Uses supplier cost × effective quantity (finalized > SO-stage) when supplier/cost
 * price is available, otherwise falls back to lineTotal.
 */
function getSOComparisonTotal(soLineItem: SalesOrderLineItem): string {
  const costBasis = soLineItem.supplierCostPrice ?? soLineItem.costPrice;
  const effectiveQty = getSOComparisonQuantity(soLineItem);
  if (costBasis != null && effectiveQty != null) {
    return String(Number(costBasis) * Number(effectiveQty));
  }
  return soLineItem.lineTotal;
}

// Helper function to get charge display name with fallback chain
function getChargeDisplayName(
  charge: Charge | undefined,
  fallback?: string,
): string {
  const defaultFallback = fallback || 'Unknown Charge';
  return (
    charge?.chargeCategory?.label ||
    charge?.chargeCategory?.value ||
    charge?.sourceReference ||
    defaultFallback
  );
}

// Helper function to compare two numeric values with tolerance
function valuesDiffer(
  val1: string | number | null | undefined,
  val2: string | number | null | undefined,
): boolean {
  const num1 = val1 === null || val1 === undefined ? 0 : Number(val1);
  const num2 = val2 === null || val2 === undefined ? 0 : Number(val2);
  if (Number.isNaN(num1) || Number.isNaN(num2)) {
    return String(val1) !== String(val2);
  }
  // Use small tolerance for floating point comparison
  return Math.abs(num1 - num2) > 0.01;
}

/** The comparison row of a line matched to charges: its accepted split, else its one charge. */
function chargeComparisonRow({
  splitParts,
  matchedCharge,
  lineTotal,
  currency,
}: {
  splitParts: readonly ConfirmedSplitPart[];
  matchedCharge: Charge | undefined;
  lineTotal: string | number | null | undefined;
  currency: string | undefined;
}): ComparisonRow | null {
  if (splitParts.length > 0) {
    const splitTotal = confirmedSplitTotal(splitParts);
    return {
      label: 'Split',
      formattedValue: formatCurrency(splitTotal, currency),
      isDifferent: valuesDiffer(lineTotal, splitTotal),
    };
  }
  if (!matchedCharge) return null;
  return {
    label: getMatchLabel(matchedCharge.docType ?? undefined),
    formattedValue: Number(matchedCharge.amount).toLocaleString(undefined, {
      style: 'currency',
      currency: matchedCharge.currencyCode || currency || 'USD',
      minimumFractionDigits: 2,
    }),
    isDifferent: valuesDiffer(lineTotal, matchedCharge.amount),
  };
}

function getMatchLabel(
  docType: string | undefined,
  gt?: ReturnType<typeof useGT>,
): string {
  const translateLabel = (label: string) => {
    if (!gt) return label;
    switch (label) {
      case 'PO':
        return gt('PO');
      case 'SO':
        return gt('SO');
      case 'Shp':
        return gt('Shp');
      default:
        return label;
    }
  };

  const label = (() => {
    switch (docType) {
      case SystemEntity.INTERNAL_PURCHASE_ORDER:
        return 'PO';
      case SystemEntity.AR_SALES_ORDER:
        return 'SO';
      case SystemEntity.SCM_SHIPMENT:
        return 'Shp';
      default:
        return '';
    }
  })();

  return translateLabel(label);
}

function getLineItemDifferenceMessages({
  invoiceLineItem,
  poLineItem,
  currency = 'USD',
}: {
  invoiceLineItem?: InvoiceLineItemDisplay;
  poLineItem?: PurchaseOrderLineItem | null;
  currency?: string;
}): string[] {
  if (!invoiceLineItem || !poLineItem) {
    return [];
  }

  const messages: string[] = [];

  if (valuesDiffer(invoiceLineItem.quantity, poLineItem.quantity)) {
    messages.push(
      `Invoice qty ${formatQuantity(invoiceLineItem.quantity)} vs PO qty ${formatQuantity(poLineItem.quantity)}`,
    );
  }

  if (valuesDiffer(invoiceLineItem.unitPrice, poLineItem.unitPrice)) {
    const poUnitPrice =
      poLineItem.unitPrice === null || poLineItem.unitPrice === undefined
        ? null
        : String(poLineItem.unitPrice);
    messages.push(
      `Invoice unit ${formatCurrency(invoiceLineItem.unitPrice, currency)} vs PO unit ${formatCurrency(poUnitPrice, currency)}`,
    );
  }

  if (valuesDiffer(invoiceLineItem.totalPrice, poLineItem.totalPrice)) {
    const poTotalPrice =
      poLineItem.totalPrice === null || poLineItem.totalPrice === undefined
        ? null
        : String(poLineItem.totalPrice);
    messages.push(
      `Invoice total ${formatCurrency(invoiceLineItem.totalPrice, currency)} vs PO total ${formatCurrency(poTotalPrice, currency)}`,
    );
  }

  return messages;
}

function DifferenceInfoIcon({
  messages,
  className,
}: {
  messages: string[];
  className?: string;
}) {
  const gt = useGT();
  if (!messages.length) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={gt('View line item discrepancies', {
            context: 'tooltip trigger for a line item comparison icon',
          })}
          className={cn(
            'flex h-4 w-4 items-center justify-center text-muted-foreground hover:text-foreground',
            className,
          )}
        >
          <InfoIcon className="h-3 w-3" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs space-y-1 text-xs">
        {messages.map((message) => (
          <p key={message}>{message}</p>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}

interface ComparisonRow {
  label: string;
  formattedValue: string;
  isDifferent?: boolean;
  tooltip?: string;
}

function ComparisonCell({ rows }: { rows: ComparisonRow[] }) {
  const gt = useGT();

  // Translate comparison row label
  const translateLabel = (label: string): string => {
    switch (label) {
      case 'Inv':
        return gt('Inv');
      case 'PO':
        return gt('PO');
      case 'SO':
        return gt('SO');
      case 'Sum':
        return gt('Sum');
      case 'Shp':
        return gt('Shp');
      default:
        return label;
    }
  };

  if (rows.length <= 1) {
    // Single value — no comparison
    return (
      <div className="text-right font-medium">
        {rows[0]?.formattedValue ?? '—'}
      </div>
    );
  }
  return (
    <div className="space-y-0.5 text-xs leading-tight">
      {rows.map((row, i) => {
        const valueEl = (
          <span
            className={cn(
              'tabular-nums',
              i === 0
                ? 'font-medium' // invoice row: always black
                : row.isDifferent
                  ? 'text-yellow-700 font-semibold' // matched row: yellow if different
                  : '', // matched row: normal if same
              row.tooltip &&
                'cursor-help underline decoration-dotted decoration-muted-foreground/40',
            )}
          >
            {row.formattedValue}
          </span>
        );
        return (
          <div
            key={row.label}
            className="flex items-baseline justify-end gap-1.5"
          >
            <span className="text-muted-foreground shrink-0">
              {translateLabel(row.label)}:
            </span>
            {row.tooltip ? (
              <Tooltip>
                <TooltipTrigger asChild>{valueEl}</TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  {row.tooltip}
                </TooltipContent>
              </Tooltip>
            ) : (
              valueEl
            )}
          </div>
        );
      })}
    </div>
  );
}

// Component to display a single PO line item in compact view
function POLineItemCompact({
  lineItem,
  invoiceLineItem,
  isMatched,
  isDisabled,
  onSelect,
  currency = 'USD',
}: {
  lineItem: PurchaseOrderLineItem;
  invoiceLineItem?: InvoiceLineItemDisplay;
  isMatched: boolean;
  isDisabled: boolean;
  onSelect: () => void;
  currency?: string;
}) {
  const gt = useGT();
  const displayName = lineItem.description || gt('No description');

  // Compare values if invoice line item is provided
  const qtyDiffers =
    invoiceLineItem &&
    valuesDiffer(invoiceLineItem.quantity, lineItem.quantity);
  const unitPriceDiffers =
    invoiceLineItem &&
    valuesDiffer(invoiceLineItem.unitPrice, lineItem.unitPrice);
  const totalPriceDiffers =
    invoiceLineItem &&
    valuesDiffer(invoiceLineItem.totalPrice, lineItem.totalPrice);
  const differenceMessages = invoiceLineItem
    ? getLineItemDifferenceMessages({
        invoiceLineItem,
        poLineItem: lineItem,
        currency,
      })
    : [];

  return (
    <CommandItem
      key={lineItem.id}
      onSelect={onSelect}
      disabled={isDisabled}
      className={cn(
        'flex items-start gap-2 py-2',
        isDisabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <CheckIcon
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          isMatched ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium truncate">{displayName}</span>
          <DifferenceInfoIcon messages={differenceMessages} />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className={qtyDiffers ? 'text-red-600' : ''}>
            <T context="quantity abbreviation">Qty:</T>{' '}
            {lineItem.quantity ?? '—'}
          </span>
          <span className={unitPriceDiffers ? 'text-red-600' : ''}>
            @ {formatCurrency(lineItem.unitPrice, currency)}
          </span>
          <span
            className={cn('font-medium', totalPriceDiffers && 'text-red-600')}
          >
            {formatCurrency(lineItem.totalPrice, currency)}
          </span>
        </div>
      </div>
    </CommandItem>
  );
}

// Helper to get a display name for an SO line item
function getSOLineItemDisplayName(
  lineItem: SalesOrderLineItem,
  gt?: ReturnType<typeof useGT>,
): string {
  const lineText = gt
    ? gt('Line {number}', { number: String(lineItem.lineItemNumber ?? '?') })
    : `Line ${lineItem.lineItemNumber ?? '?'}`;
  return (
    lineItem.productName ||
    lineItem.productSku ||
    lineItem.customerProductSku ||
    lineText
  );
}

// Component to display a single SO line item in compact view
function SOLineItemCompact({
  lineItem,
  invoiceLineItem,
  isMatched,
  isDisabled,
  onSelect,
  currency = 'USD',
}: {
  lineItem: SalesOrderLineItem;
  invoiceLineItem?: InvoiceLineItemDisplay;
  isMatched: boolean;
  isDisabled: boolean;
  onSelect: () => void;
  currency?: string;
}) {
  const gt = useGT();
  const displayName = getSOLineItemDisplayName(lineItem, gt);

  const soUnitPrice = getSOComparisonUnitPrice(lineItem);
  const soTotalPrice = getSOComparisonTotal(lineItem);

  // Compare values if invoice line item is provided
  const qtyDiffers =
    invoiceLineItem &&
    valuesDiffer(invoiceLineItem.quantity, lineItem.quantity);
  const unitPriceDiffers =
    invoiceLineItem && valuesDiffer(invoiceLineItem.unitPrice, soUnitPrice);
  const totalPriceDiffers =
    invoiceLineItem && valuesDiffer(invoiceLineItem.totalPrice, soTotalPrice);

  // Build SO-specific difference messages for tooltip
  const differenceMessages: string[] = [];
  if (invoiceLineItem) {
    if (qtyDiffers) {
      differenceMessages.push(
        `Invoice qty ${formatQuantity(invoiceLineItem.quantity)} vs SO qty ${formatQuantity(lineItem.quantity)}`,
      );
    }
    if (unitPriceDiffers) {
      differenceMessages.push(
        `Invoice unit ${formatCurrency(invoiceLineItem.unitPrice, currency)} vs SO cost ${formatCurrency(soUnitPrice, currency)}`,
      );
    }
    if (totalPriceDiffers) {
      differenceMessages.push(
        `Invoice total ${formatCurrency(invoiceLineItem.totalPrice, currency)} vs SO cost total ${formatCurrency(soTotalPrice, currency)}`,
      );
    }
  }

  return (
    <CommandItem
      key={lineItem.id}
      onSelect={onSelect}
      disabled={isDisabled}
      className={cn(
        'flex items-start gap-2 py-2',
        isDisabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <CheckIcon
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          isMatched ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium truncate">{displayName}</span>
          <DifferenceInfoIcon messages={differenceMessages} />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className={qtyDiffers ? 'text-red-600' : ''}>
            <T context="quantity abbreviation">Qty:</T>{' '}
            {lineItem.quantity ?? '—'}
          </span>
          <span className={unitPriceDiffers ? 'text-red-600' : ''}>
            @ {formatCurrency(soUnitPrice, currency)}
          </span>
          <span
            className={cn('font-medium', totalPriceDiffers && 'text-red-600')}
          >
            {formatCurrency(soTotalPrice, currency)}
          </span>
        </div>
      </div>
    </CommandItem>
  );
}

// Component to display a single charge in compact view (for any document type)
function ChargeCompact({
  charge,
  invoiceLineItem,
  isMatched,
  onSelect,
  showParentDocument = false,
}: {
  charge: Charge;
  invoiceLineItem?: InvoiceLineItemDisplay;
  isMatched: boolean;
  onSelect: () => void;
  showParentDocument?: boolean;
}) {
  // Get charge category label (longer description) or fall back to value/sourceReference
  const displayName = getChargeDisplayName(charge);

  const chargeAmount = Number(charge.amount);
  const chargeAmountFormatted = chargeAmount.toLocaleString(undefined, {
    style: 'currency',
    currency: charge.currencyCode || 'USD',
    minimumFractionDigits: 2,
  });

  // Compare amount if invoice line item is provided
  const invoiceAmount = invoiceLineItem?.totalPrice
    ? Number(invoiceLineItem.totalPrice)
    : null;
  const amountDiffers =
    invoiceAmount !== null && Math.abs(chargeAmount - invoiceAmount) > 0.01;

  return (
    <CommandItem
      key={charge.id}
      onSelect={onSelect}
      className="flex items-start gap-2 py-2"
    >
      <CheckIcon
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          isMatched ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-1.5">
          <GitDiffIcon className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm font-medium truncate">{displayName}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className={amountDiffers ? 'text-red-600 font-medium' : ''}>
            {chargeAmountFormatted}
          </span>
          <span className="text-muted-foreground/60">
            {charge.direction === 'DEBIT' ? <T>Debit</T> : <T>Credit</T>}
          </span>
          {showParentDocument && charge.docType && charge.docId && (
            <SystemEntityInline
              entityType={charge.docType as SystemEntity}
              entityId={charge.docId}
              className="text-[10px] h-4 py-0"
            />
          )}
        </div>
      </div>
    </CommandItem>
  );
}

// Component to fetch and display charges for any document type (PO, SO, Shipment)
function DocumentChargesSection({
  docType,
  docId,
  matchedChargeId,
  alreadyMatchedChargeIds,
  currentMatchedChargeId,
  invoiceLineItem,
  onSelectCharge,
  label,
  icon: Icon = CurrencyDollarIcon,
}: {
  docType: SystemEntity;
  docId: string;
  matchedChargeId: string | null;
  alreadyMatchedChargeIds: Set<string>;
  currentMatchedChargeId: string | null;
  invoiceLineItem?: InvoiceLineItemDisplay;
  onSelectCharge: (chargeId: string) => void;
  label?: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  const gt = useGT();
  const { charges, isLoading } = useChargesForEntity({
    entityType: docType as ChargeableEntity,
    entityId: docId,
    includeLineCharges: false, // Header charges only for matching
    enabled: !!docId,
  });

  if (isLoading) {
    return (
      <div className="px-2 py-2 text-xs text-muted-foreground">
        <T>Loading charges...</T>
      </div>
    );
  }

  if (!charges || charges.length === 0) {
    return null; // Don't show anything if no charges
  }

  // Filter out charges that are already matched to OTHER invoice line items
  const unmatchedCharges = charges.filter((charge) => {
    // Always show the currently matched charge for this line item
    if (currentMatchedChargeId === charge.id) {
      return true;
    }
    // For other charges, only show if not matched
    return !alreadyMatchedChargeIds.has(charge.id);
  });

  if (unmatchedCharges.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      <div className="px-2 py-1 text-xs font-medium text-muted-foreground flex items-center gap-1.5">
        <Icon className="h-3 w-3" />
        {label || gt('Charges')}
      </div>
      {unmatchedCharges.map((charge) => (
        <ChargeCompact
          key={charge.id}
          charge={charge}
          invoiceLineItem={invoiceLineItem}
          isMatched={matchedChargeId === charge.id}
          onSelect={() => onSelectCharge(charge.id)}
        />
      ))}
    </div>
  );
}

// Backwards compatible alias for PO charges
function POChargesSection(props: {
  poId: string;
  matchedChargeId: string | null;
  alreadyMatchedChargeIds: Set<string>;
  currentMatchedChargeId: string | null;
  invoiceLineItem?: InvoiceLineItemDisplay;
  onSelectCharge: (chargeId: string) => void;
}) {
  return (
    <DocumentChargesSection
      docType={SystemEntity.INTERNAL_PURCHASE_ORDER}
      docId={props.poId}
      matchedChargeId={props.matchedChargeId}
      alreadyMatchedChargeIds={props.alreadyMatchedChargeIds}
      currentMatchedChargeId={props.currentMatchedChargeId}
      invoiceLineItem={props.invoiceLineItem}
      onSelectCharge={props.onSelectCharge}
    />
  );
}

// Component to fetch and display PO details with line items
function POLineItemsSection({
  poId,
  matchedLineItemId,
  alreadyMatchedPOLineItemIds,
  currentLineItemMatchedId,
  invoiceLineItem,
  onSelectLineItem,
  currency,
}: {
  poId: string;
  matchedLineItemId: string | null;
  alreadyMatchedPOLineItemIds: Set<string>;
  currentLineItemMatchedId: string | null;
  invoiceLineItem?: InvoiceLineItemDisplay;
  onSelectLineItem: (lineItemId: string) => void;
  currency?: string;
}) {
  const { data, isLoading } = usePurchaseOrder(poId, !!poId);

  if (isLoading) {
    return (
      <div className="px-2 py-3 text-sm text-muted-foreground">
        <T>Loading line items...</T>
      </div>
    );
  }

  if (!data?.lineItems || data.lineItems.length === 0) {
    return (
      <div className="px-2 py-2 text-xs text-muted-foreground">
        <T>No line items found</T>
      </div>
    );
  }

  // Filter out line items that are already matched to OTHER invoice line items
  // But include the current line item's match (if any) so it shows in the list
  const unmatchedLineItems = data.lineItems.filter((lineItem) => {
    // Always show the currently matched item for this line item (so you can see what's matched and unmatch it)
    if (currentLineItemMatchedId === lineItem.id) {
      return true;
    }
    // For all other items, only show if they're NOT matched to any invoice line item
    return !alreadyMatchedPOLineItemIds.has(lineItem.id);
  });

  if (unmatchedLineItems.length === 0) {
    return (
      <div className="px-2 py-2 text-xs text-muted-foreground">
        <T>No unmatched line items</T>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {unmatchedLineItems.map((lineItem) => (
        <POLineItemCompact
          key={lineItem.id}
          lineItem={lineItem}
          invoiceLineItem={invoiceLineItem}
          isMatched={matchedLineItemId === lineItem.id}
          isDisabled={false}
          onSelect={() => onSelectLineItem(lineItem.id)}
          currency={currency}
        />
      ))}
    </div>
  );
}

// Component to fetch and display SO details with line items
function SOLineItemsSection({
  soId,
  matchedLineItemId,
  alreadyMatchedSOLineItemIds,
  currentLineItemMatchedId,
  invoiceLineItem,
  onSelectLineItem,
  currency,
}: {
  soId: string;
  matchedLineItemId: string | null;
  alreadyMatchedSOLineItemIds: Set<string>;
  currentLineItemMatchedId: string | null;
  invoiceLineItem?: InvoiceLineItemDisplay;
  onSelectLineItem: (lineItemId: string) => void;
  currency?: string;
}) {
  const { lineItems, isLoading } = useSalesOrder(soId, !!soId);

  if (isLoading) {
    return (
      <div className="px-2 py-3 text-sm text-muted-foreground">
        <T>Loading line items...</T>
      </div>
    );
  }

  if (!lineItems || lineItems.length === 0) {
    return (
      <div className="px-2 py-2 text-xs text-muted-foreground">
        <T>No line items found</T>
      </div>
    );
  }

  // Filter out line items that are already matched to OTHER invoice line items
  const unmatchedLineItems = lineItems.filter((lineItem) => {
    if (currentLineItemMatchedId === lineItem.id) {
      return true;
    }
    return !alreadyMatchedSOLineItemIds.has(lineItem.id);
  });

  if (unmatchedLineItems.length === 0) {
    return (
      <div className="px-2 py-2 text-xs text-muted-foreground">
        <T>No unmatched line items</T>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {unmatchedLineItems.map((lineItem) => (
        <SOLineItemCompact
          key={lineItem.id}
          lineItem={lineItem}
          invoiceLineItem={invoiceLineItem}
          isMatched={matchedLineItemId === lineItem.id}
          isDisabled={false}
          onSelect={() => onSelectLineItem(lineItem.id)}
          currency={currency}
        />
      ))}
    </div>
  );
}

/**
 * Build the title + detail line shown for a supporting-document match.
 * For receipts we surface the receipt # and total amount from the parsed
 * summary; otherwise we fall back to the file name.
 */
function getDocumentMatchDisplay(
  doc: SupportingDocItem | undefined,
  gt: ReturnType<typeof useGT>,
  currency?: string,
): { title: string; detail: string | null } {
  if (!doc) {
    return { title: gt('Document'), detail: null };
  }
  const title = getClassificationLabel(doc.classificationType);
  if (doc.summary?.classificationType === 'receipt') {
    const r = doc.summary;
    const amount =
      r.totalAmount !== null
        ? formatCurrency(String(r.totalAmount), r.currency || currency || 'USD')
        : null;
    const detail =
      [r.receiptNumber ? `#${r.receiptNumber}` : null, amount]
        .filter((v): v is string => !!v)
        .join(' · ') ||
      doc.originalFileName ||
      null;
    return { title, detail };
  }
  return { title, detail: doc.originalFileName ?? null };
}

/**
 * Chip rendered in the "Matched To" column when an invoice line is
 * substantiated by a supporting document (e.g. a Receipt backing a lumper
 * line). Clicking opens the document; the optional menu confirms/removes.
 */
function DocumentMatchChip({
  doc,
  isTentative,
  showActions,
  currency,
  onConfirm,
  onRemove,
}: {
  doc?: SupportingDocItem;
  isTentative: boolean;
  showActions: boolean;
  currency?: string;
  onConfirm: () => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const [opening, setOpening] = useState(false);
  const { title, detail } = getDocumentMatchDisplay(doc, gt, currency);

  const handleOpen = async () => {
    if (!doc || opening) return;
    setOpening(true);
    // Open the tab synchronously inside the click gesture so it isn't
    // popup-blocked, then navigate it once the signed URL resolves. We can't
    // pass "noopener" here (it makes window.open return null), so null the
    // opener manually to keep the same reverse-tabnabbing protection.
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const preview = await rpcClient.core.documents.getPreview({
        documentId: doc.documentId,
      });
      if (tab) {
        tab.location.href = preview.signedUrl;
      } else {
        window.open(preview.signedUrl, '_blank', 'noopener,noreferrer');
      }
      setOpening(false);
    } catch (err) {
      tab?.close();
      setOpening(false);
      showToast({
        title: gt('Could not open document'),
        message: err instanceof Error ? err.message : gt('Unknown error'),
        color: 'error',
        icon: 'x',
      });
    }
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 border rounded-md px-2 py-1 max-w-[220px]',
        isTentative
          ? 'border-amber-400 bg-amber-50'
          : 'border-green-500 bg-green-50',
      )}
    >
      <FileTextIcon
        className={cn(
          'h-4 w-4 shrink-0',
          isTentative ? 'text-amber-500' : 'text-green-600',
        )}
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void handleOpen();
        }}
        disabled={opening || !doc}
        className="min-w-0 flex-1 text-left"
      >
        <div className="flex items-center gap-1 text-xs font-medium truncate">
          <span className="truncate">{title}</span>
          <ArrowSquareOutIcon className="h-3 w-3 text-muted-foreground shrink-0" />
        </div>
        {detail && (
          <div className="text-[10px] text-muted-foreground truncate">
            {detail}
          </div>
        )}
      </button>
      {showActions && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <DotsThreeIcon className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {isTentative && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onConfirm();
                }}
              >
                <T>Confirm match</T>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="text-destructive focus:text-destructive"
            >
              <T>Remove match</T>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

interface DocumentMatchCellProps {
  lineItemId: string;
  canEditMatches: boolean;
  // Pre-fetched associations for this line item to avoid N+1 queries
  lineItemAssociations?: EntityAssociationItem[];
  // Pre-fetched matched supporting document (e.g. a Receipt substantiating a
  // lumper line). Resolved by documentId from the invoice's supporting docs.
  matchedDocument?: SupportingDocItem;
  currency?: string;
}

// Dedicated cell for the "Document" column: a supporting document (e.g. a
// Receipt / proof-of-delivery) that substantiates an invoice line. This is the
// 3rd-way match — distinct from a true PO/SO/charge match — so it gets its own
// column rather than competing for the "Matched To" slot. Documents are never
// aggregated, so this renders independently of any aggregation grouping.
function DocumentMatchCell({
  lineItemId,
  canEditMatches,
  lineItemAssociations: lineItemAssociationsRaw,
  matchedDocument,
  currency,
}: DocumentMatchCellProps) {
  const lineItemAssociations = lineItemAssociationsRaw ?? EMPTY_ASSOCIATIONS;
  const invoiceCurrency = currency || 'USD';

  const deleteMutation = useDeleteEntityAssociation();
  const confirmMutation = useConfirmEntityAssociationMatch();

  const documentMatch =
    lineItemAssociations.find(
      (assoc) => assoc.entity.entityType === SystemEntity.CORE_DOCUMENT,
    ) || null;

  const isTentative =
    documentMatch != null && isTentativeMatch(documentMatch.metadata);

  if (!documentMatch) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const handleConfirmMatch = () => {
    confirmMutation.mutate({
      sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
      sourceEntityId: lineItemId,
      targetEntityType: SystemEntity.CORE_DOCUMENT,
      targetEntityId: documentMatch.entity.entityId,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
  };

  const handleDeleteMatch = () => {
    deleteMutation.mutate({
      sourceEntityId: lineItemId,
      sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
      targetEntityId: documentMatch.entity.entityId,
      targetEntityType: SystemEntity.CORE_DOCUMENT,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
  };

  return (
    <DocumentMatchChip
      doc={matchedDocument}
      isTentative={isTentative}
      showActions={canEditMatches}
      currency={invoiceCurrency}
      onConfirm={handleConfirmMatch}
      onRemove={handleDeleteMatch}
    />
  );
}

// Separate component for the matching cell to handle its own hooks and state
function MatchingCell({
  lineItemId,
  lineItem,
  canEditMatches,
  associatedPOs,
  associatedSOs,
  associatedShipments,
  lineItemAssociations: lineItemAssociationsRaw,
  allMatchedPOLineItemIds,
  allMatchedSOLineItemIds,
  allMatchedChargeIds,
  matchedPOLineItem,
  matchedSOLineItem,
  matchedCharge,
  currency,
  aggregationInfo,
}: MatchingCellProps) {
  const gt = useGT();
  const lineItemAssociations = lineItemAssociationsRaw ?? EMPTY_ASSOCIATIONS;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const invoiceCurrency = currency || 'USD';

  const createMutation = useCreateEntityAssociation();
  const deleteMutation = useDeleteEntityAssociation();
  const confirmMutation = useConfirmEntityAssociationMatch();

  // Find the matched PO line item from pre-fetched associations
  const poLineItemMatch =
    lineItemAssociations.find(
      (assoc) =>
        assoc.entity.entityType ===
        SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
    ) || null;

  // Find the matched SO line item from pre-fetched associations
  const soLineItemMatch =
    lineItemAssociations.find(
      (assoc) =>
        assoc.entity.entityType === SystemEntity.AR_SALES_ORDER_LINE_ITEM,
    ) || null;

  // Find matched charge from pre-fetched associations
  const chargeMatch =
    lineItemAssociations.find(
      (assoc) => assoc.entity.entityType === SystemEntity.CHARGE,
    ) || null;

  // Unified line item match: PO or SO line item (PO takes precedence)
  const lineItemMatch = poLineItemMatch || soLineItemMatch;
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _isSOLineItemMatch = !poLineItemMatch && !!soLineItemMatch;

  // Determine which type of match we have. Precedence: line item > charge.
  // Supporting-document (3rd-way) matches render in their own "Document" column
  // via DocumentMatchCell, so they're intentionally excluded here.
  const match = lineItemMatch || chargeMatch;
  const isChargeMatch = !lineItemMatch && !!chargeMatch;

  // Confidence state: checks locked-in matchStatus first, falls back to
  // confidence threshold for legacy records (mirrors backend classifyMatchStatus)
  const isTentative = match != null && isTentativeMatch(match.metadata);

  const handleConfirmMatch = () => {
    if (!match) return;
    confirmMutation.mutate({
      sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
      sourceEntityId: lineItemId,
      targetEntityType: match.entity.entityType,
      targetEntityId: match.entity.entityId,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
  };

  // Get display description from whichever type of line item is matched
  const matchedDescription = matchedPOLineItem
    ? matchedPOLineItem.description || gt('Unnamed item')
    : matchedSOLineItem
      ? getSOLineItemDisplayName(matchedSOLineItem, gt)
      : gt('Unnamed item');

  // Check if values differ between invoice line item and matched line item (PO or SO)
  const hasDifferences = useMemo(() => {
    if (matchedPOLineItem) {
      return (
        valuesDiffer(lineItem.quantity, matchedPOLineItem.quantity) ||
        valuesDiffer(lineItem.unitPrice, matchedPOLineItem.unitPrice) ||
        valuesDiffer(lineItem.totalPrice, matchedPOLineItem.totalPrice)
      );
    }
    if (matchedSOLineItem) {
      return (
        valuesDiffer(
          lineItem.quantity,
          getSOComparisonQuantity(matchedSOLineItem),
        ) ||
        valuesDiffer(
          lineItem.unitPrice,
          getSOComparisonUnitPrice(matchedSOLineItem),
        ) ||
        valuesDiffer(
          lineItem.totalPrice,
          getSOComparisonTotal(matchedSOLineItem),
        )
      );
    }
    return false;
  }, [lineItem, matchedPOLineItem, matchedSOLineItem]);

  const differenceMessages = useMemo(() => {
    if (matchedPOLineItem) {
      return getLineItemDifferenceMessages({
        invoiceLineItem: lineItem,
        poLineItem: matchedPOLineItem,
        currency: invoiceCurrency,
      });
    }
    if (matchedSOLineItem) {
      const messages: string[] = [];
      const soComparisonPrice = getSOComparisonUnitPrice(matchedSOLineItem);
      const soComparisonQty = getSOComparisonQuantity(matchedSOLineItem);
      const soComparisonTotal = getSOComparisonTotal(matchedSOLineItem);
      if (valuesDiffer(lineItem.quantity, soComparisonQty)) {
        // Label reflects whether we're comparing against finalized or SO-stage qty
        const soQtyLabel =
          matchedSOLineItem.supplierQuantity != null
            ? 'SO finalized qty'
            : 'SO qty';
        messages.push(
          `Invoice qty ${formatQuantity(lineItem.quantity)} vs ${soQtyLabel} ${formatQuantity(
            typeof soComparisonQty === 'number'
              ? String(soComparisonQty)
              : soComparisonQty,
          )}`,
        );
      }
      if (valuesDiffer(lineItem.unitPrice, soComparisonPrice)) {
        messages.push(
          `Invoice unit ${formatCurrency(lineItem.unitPrice, invoiceCurrency)} vs SO cost ${formatCurrency(soComparisonPrice, invoiceCurrency)}`,
        );
      }
      if (valuesDiffer(lineItem.totalPrice, soComparisonTotal)) {
        messages.push(
          `Invoice total ${formatCurrency(lineItem.totalPrice, invoiceCurrency)} vs SO cost total ${formatCurrency(soComparisonTotal, invoiceCurrency)}`,
        );
      }
      return messages;
    }
    return [];
  }, [invoiceCurrency, lineItem, matchedPOLineItem, matchedSOLineItem]);

  // Build a set of matched PO line item IDs excluding the current line item's match
  // This ensures that when you unmatch, the item becomes available again
  const otherMatchedPOLineItemIds = useMemo(() => {
    const otherMatched = new Set(allMatchedPOLineItemIds);
    if (poLineItemMatch?.entity.entityId) {
      otherMatched.delete(poLineItemMatch.entity.entityId);
    }
    return otherMatched;
  }, [allMatchedPOLineItemIds, poLineItemMatch]);

  // Build a set of matched SO line item IDs excluding the current line item's match
  const otherMatchedSOLineItemIds = useMemo(() => {
    const otherMatched = new Set(allMatchedSOLineItemIds);
    if (soLineItemMatch?.entity.entityId) {
      otherMatched.delete(soLineItemMatch.entity.entityId);
    }
    return otherMatched;
  }, [allMatchedSOLineItemIds, soLineItemMatch]);

  // Build a set of matched charge IDs excluding the current line item's match
  const otherMatchedChargeIds = useMemo(() => {
    const otherMatched = new Set(allMatchedChargeIds);
    if (chargeMatch?.entity.entityId) {
      otherMatched.delete(chargeMatch.entity.entityId);
    }
    return otherMatched;
  }, [allMatchedChargeIds, chargeMatch]);

  // Member of an aggregation group → the real match chip is drawn on the
  // group's subtotal row, so we render a muted marker here to keep the row
  // readable and avoid implying a per-line comparison with the shared target.
  // Placed after all hooks to satisfy rules-of-hooks.
  if (aggregationInfo) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <GitDiffIcon className="h-3 w-3" />
        <span>
          <T context="label for a line item that is part of an aggregated match group">
            Part of aggregated match (<Var>{aggregationInfo.memberIndex}</Var>/
            <Var>{aggregationInfo.memberCount}</Var>)
          </T>
        </span>
      </div>
    );
  }

  const handleCreateLineItemMatch = async (poLineItemId: string) => {
    // If there's an existing match, delete it first
    if (match) {
      await deleteMutation.mutateAsync({
        sourceEntityId: lineItemId,
        sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
        targetEntityId: match.entity.entityId,
        targetEntityType: match.entity.entityType,
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
      });
    }

    // Create the new match to PO line item
    await createMutation.mutateAsync({
      sourceEntityId: lineItemId,
      sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
      targetEntityId: poLineItemId,
      targetEntityType: SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
    setIsPopoverOpen(false);
  };

  const handleCreateSOLineItemMatch = async (soLineItemId: string) => {
    // If there's an existing match, delete it first
    if (match) {
      await deleteMutation.mutateAsync({
        sourceEntityId: lineItemId,
        sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
        targetEntityId: match.entity.entityId,
        targetEntityType: match.entity.entityType,
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
      });
    }

    // Create the new match to SO line item
    await createMutation.mutateAsync({
      sourceEntityId: lineItemId,
      sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
      targetEntityId: soLineItemId,
      targetEntityType: SystemEntity.AR_SALES_ORDER_LINE_ITEM,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
    setIsPopoverOpen(false);
  };

  const handleCreateChargeMatch = async (chargeId: string) => {
    // If there's an existing match, delete it first
    if (match) {
      await deleteMutation.mutateAsync({
        sourceEntityId: lineItemId,
        sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
        targetEntityId: match.entity.entityId,
        targetEntityType: match.entity.entityType,
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
      });
    }

    // Create the new match to charge
    await createMutation.mutateAsync({
      sourceEntityId: lineItemId,
      sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
      targetEntityId: chargeId,
      targetEntityType: SystemEntity.CHARGE,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
    setIsPopoverOpen(false);
  };

  const handleDeleteMatch = async () => {
    if (match) {
      await deleteMutation.mutateAsync({
        sourceEntityId: lineItemId,
        sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
        targetEntityId: match.entity.entityId,
        targetEntityType: match.entity.entityType,
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
      });
    }
  };

  // Get display name for matched charge
  const matchedChargeDescription = getChargeDisplayName(
    matchedCharge,
    gt('Charge'),
  );

  // Get parent document info from matched charge
  const chargeParentDocType = matchedCharge?.docType as
    SystemEntity | undefined;
  const chargeParentDocId = matchedCharge?.docId;

  // Surface the charge amount alongside the label so AP staff can confirm at a
  // glance that the matched charge is the right one (e.g. the $1,800 freight
  // row vs. a $0.50 consolidation line on the same TO).
  const matchedChargeAmount =
    matchedCharge?.amount != null ? String(matchedCharge.amount) : null;
  const matchedChargeFormattedAmount = matchedChargeAmount
    ? formatCurrency(
        matchedChargeAmount,
        matchedCharge?.currencyCode || currency,
      )
    : null;

  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _isHoldPay = getActiveHoldPay(
    matchedPOLineItem,
    matchedSOLineItem,
    matchedCharge,
  );
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _isConsign = getActiveConsign(matchedSOLineItem);
  const { troubleData: matchTroubleData, origin: matchTroubleOrigin } =
    getMatchTrouble(matchedPOLineItem, matchedSOLineItem, matchedCharge);

  // Whether the current match was created manually by a user vs. produced
  // automatically (reconciliation / AI agent). Drives the Manual/Auto badge.
  const isManual = match != null && isManualMatch(match.metadata);

  // The match chip has several shapes (charge, line item; read-only and
  // interactive). Render whichever applies here, then the wrapper below appends
  // a single Manual/Auto badge so every shape stays consistent.
  function renderMatchChip() {
    if (!canEditMatches) {
      // Read-only display
      if (isChargeMatch) {
        return (
          <div
            className={cn(
              'flex items-center gap-2 border rounded-md px-2 py-1 max-w-[220px]',
              isTentative
                ? 'border-amber-400 bg-amber-50'
                : 'border-green-500 bg-green-50',
            )}
          >
            <GitDiffIcon
              className={cn(
                'h-4 w-4 shrink-0',
                isTentative ? 'text-amber-500' : 'text-green-600',
              )}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-xs font-medium truncate">
                  {matchedChargeDescription}
                </div>
                {matchedChargeFormattedAmount && (
                  <div className="text-xs font-medium tabular-nums shrink-0">
                    {matchedChargeFormattedAmount}
                  </div>
                )}
              </div>
              {chargeParentDocType && chargeParentDocId && (
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <T context="preposition, as in 'from a document'">from</T>
                  <SystemEntityInline
                    entityType={chargeParentDocType}
                    entityId={chargeParentDocId}
                    className="text-[10px] h-4 py-0"
                  />
                </div>
              )}
              <TroubleDataBadges
                troubleData={matchTroubleData}
                origin={matchTroubleOrigin}
              />
            </div>
          </div>
        );
      }

      return match ? (
        <div
          className={cn(
            'flex items-center gap-2 border rounded-md px-2 py-1 max-w-[200px]',
            hasDifferences
              ? 'border-yellow-500 bg-yellow-50'
              : isTentative
                ? 'border-amber-400 bg-amber-50'
                : 'border-green-500 bg-green-50',
          )}
        >
          {hasDifferences ? (
            <WarningIcon className="h-4 w-4 text-yellow-600 shrink-0" />
          ) : isTentative ? (
            <WarningIcon className="h-4 w-4 text-amber-500 shrink-0" />
          ) : (
            <CheckIcon className="h-4 w-4 text-green-600 shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate">
              {matchedDescription}
            </div>
            <TroubleDataBadges
              troubleData={matchTroubleData}
              origin={matchTroubleOrigin}
            />
          </div>
          <DifferenceInfoIcon
            messages={differenceMessages}
            className="ml-1 shrink-0"
          />
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">
          <T>Not matched</T>
        </span>
      );
    }

    // Interactive matching
    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal>
        <PopoverTrigger asChild>
          {match ? (
            isChargeMatch ? (
              // Charge match display
              <div
                className={cn(
                  'flex items-center gap-2 border rounded-md px-2 py-1 transition-colors cursor-pointer min-w-0 max-w-[220px]',
                  isTentative
                    ? 'border-amber-400 bg-amber-50 hover:bg-amber-100'
                    : 'border-green-500 bg-green-50 hover:bg-green-100',
                )}
              >
                <GitDiffIcon
                  className={cn(
                    'h-4 w-4 shrink-0',
                    isTentative ? 'text-amber-500' : 'text-green-600',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-xs font-medium truncate">
                      {matchedChargeDescription}
                    </div>
                    {matchedChargeFormattedAmount && (
                      <div className="text-xs font-medium tabular-nums shrink-0">
                        {matchedChargeFormattedAmount}
                      </div>
                    )}
                  </div>
                  {chargeParentDocType && chargeParentDocId && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <span>
                        <T context="preposition indicating source document for a charge match">
                          from
                        </T>
                      </span>
                      <SystemEntityInline
                        entityType={chargeParentDocType}
                        entityId={chargeParentDocId}
                        className="text-[10px] h-4 py-0"
                      />
                    </div>
                  )}
                  <TroubleDataBadges
                    troubleData={matchTroubleData}
                    origin={matchTroubleOrigin}
                  />
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DotsThreeIcon className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isTentative && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfirmMatch();
                        }}
                      >
                        <T>Confirm match</T>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMatch();
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <T>Remove match</T>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              // Line item match display
              <div
                className={cn(
                  'flex items-center gap-2 border rounded-md px-2 py-1 transition-colors cursor-pointer min-w-0 max-w-[200px]',
                  hasDifferences
                    ? 'border-yellow-500 bg-yellow-50 hover:bg-yellow-100'
                    : isTentative
                      ? 'border-amber-400 bg-amber-50 hover:bg-amber-100'
                      : 'border-green-500 bg-green-50 hover:bg-green-100',
                )}
              >
                {hasDifferences ? (
                  <WarningIcon className="h-4 w-4 text-yellow-600 shrink-0" />
                ) : isTentative ? (
                  <WarningIcon className="h-4 w-4 text-amber-500 shrink-0" />
                ) : (
                  <CheckIcon className="h-4 w-4 text-green-600 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">
                    {matchedDescription}
                  </div>
                  <TroubleDataBadges
                    troubleData={matchTroubleData}
                    origin={matchTroubleOrigin}
                  />
                </div>
                <DifferenceInfoIcon
                  messages={differenceMessages}
                  className="ml-1 shrink-0"
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DotsThreeIcon className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isTentative && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfirmMatch();
                        }}
                      >
                        <T>Confirm match</T>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMatch();
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <T>Remove match</T>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )
          ) : (
            <Button variant="outline" size="sm" className="h-7 text-xs">
              <PlusIcon className="h-3 w-3 mr-1" />
              <T>Match</T>
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="w-[400px] p-0"
          align="start"
          // Base UI's spelling of Radix's `onOpenAutoFocus` + `preventDefault`:
          // open without pulling focus, so the Command input below keeps it.
          initialFocus={false}
        >
          <Command>
            <CommandInput placeholder={gt('Search line items or charges...')} />
            <CommandList>
              <CommandEmpty>
                {associatedPOs.length === 0 &&
                associatedSOs.length === 0 &&
                associatedShipments.length === 0
                  ? gt(
                      'No associated documents found. Link an invoice to a PO, SO, or Shipment first.',
                    )
                  : gt('No matching items found.')}
              </CommandEmpty>
              {/* Purchase Orders - with line items and charges */}
              {associatedPOs.map((po, index) => {
                const poId = po.entity.entityId;
                return (
                  <React.Fragment key={poId}>
                    {index > 0 && <CommandSeparator />}
                    <CommandGroup
                      heading={
                        <div className="flex items-center gap-2">
                          <PackageIcon className="h-3 w-3" />
                          <SystemEntityInline
                            entityType={po.entity.entityType}
                            entityId={po.entity.entityId}
                            className="text-xs"
                          />
                        </div>
                      }
                    >
                      <POLineItemsSection
                        poId={poId}
                        matchedLineItemId={
                          lineItemMatch?.entity.entityId || null
                        }
                        alreadyMatchedPOLineItemIds={otherMatchedPOLineItemIds}
                        currentLineItemMatchedId={
                          lineItemMatch?.entity.entityId || null
                        }
                        invoiceLineItem={lineItem}
                        onSelectLineItem={handleCreateLineItemMatch}
                        currency={invoiceCurrency}
                      />
                      <POChargesSection
                        poId={poId}
                        matchedChargeId={chargeMatch?.entity.entityId || null}
                        alreadyMatchedChargeIds={otherMatchedChargeIds}
                        currentMatchedChargeId={
                          chargeMatch?.entity.entityId || null
                        }
                        invoiceLineItem={lineItem}
                        onSelectCharge={handleCreateChargeMatch}
                      />
                    </CommandGroup>
                  </React.Fragment>
                );
              })}
              {/* Sales Orders - with line items and charges */}
              {associatedSOs.length > 0 && associatedPOs.length > 0 && (
                <CommandSeparator />
              )}
              {associatedSOs.map((so, index) => {
                const soId = so.entity.entityId;
                return (
                  <React.Fragment key={soId}>
                    {index > 0 && <CommandSeparator />}
                    <CommandGroup
                      heading={
                        <div className="flex items-center gap-2">
                          <ShoppingCartIcon className="h-3 w-3" />
                          <SystemEntityInline
                            entityType={so.entity.entityType}
                            entityId={so.entity.entityId}
                            className="text-xs"
                          />
                        </div>
                      }
                    >
                      <SOLineItemsSection
                        soId={soId}
                        matchedLineItemId={
                          soLineItemMatch?.entity.entityId || null
                        }
                        alreadyMatchedSOLineItemIds={otherMatchedSOLineItemIds}
                        currentLineItemMatchedId={
                          soLineItemMatch?.entity.entityId || null
                        }
                        invoiceLineItem={lineItem}
                        onSelectLineItem={handleCreateSOLineItemMatch}
                        currency={invoiceCurrency}
                      />
                      <DocumentChargesSection
                        docType={SystemEntity.AR_SALES_ORDER}
                        docId={soId}
                        matchedChargeId={chargeMatch?.entity.entityId || null}
                        alreadyMatchedChargeIds={otherMatchedChargeIds}
                        currentMatchedChargeId={
                          chargeMatch?.entity.entityId || null
                        }
                        invoiceLineItem={lineItem}
                        onSelectCharge={handleCreateChargeMatch}
                        label={gt('SO Charges', {
                          context:
                            'label for sales order charges section in invoice matching',
                        })}
                        icon={ShoppingCartIcon}
                      />
                    </CommandGroup>
                  </React.Fragment>
                );
              })}
              {/* Shipments - charges only */}
              {associatedShipments.length > 0 &&
                (associatedPOs.length > 0 || associatedSOs.length > 0) && (
                  <CommandSeparator />
                )}
              {associatedShipments.map((shipment, index) => {
                const shipmentId = shipment.entity.entityId;
                return (
                  <React.Fragment key={shipmentId}>
                    {index > 0 && <CommandSeparator />}
                    <CommandGroup
                      heading={
                        <div className="flex items-center gap-2">
                          <TruckIcon className="h-3 w-3" />
                          <SystemEntityInline
                            entityType={shipment.entity.entityType}
                            entityId={shipment.entity.entityId}
                            className="text-xs"
                          />
                        </div>
                      }
                    >
                      <DocumentChargesSection
                        docType={SystemEntity.SCM_SHIPMENT}
                        docId={shipmentId}
                        matchedChargeId={chargeMatch?.entity.entityId || null}
                        alreadyMatchedChargeIds={otherMatchedChargeIds}
                        currentMatchedChargeId={
                          chargeMatch?.entity.entityId || null
                        }
                        invoiceLineItem={lineItem}
                        onSelectCharge={handleCreateChargeMatch}
                        label={gt('Shipment Charges', {
                          context:
                            'label for shipment charges section in invoice matching',
                        })}
                        icon={TruckIcon}
                      />
                    </CommandGroup>
                  </React.Fragment>
                );
              })}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  // No match → render the chip (its own "Not matched" state) without a badge.
  if (!match) return renderMatchChip();

  return (
    <div className="flex flex-col items-start gap-1">
      {renderMatchChip()}
      <span
        className={cn(
          'inline-flex items-center rounded px-1 text-[10px] font-medium leading-4',
          isManual
            ? 'bg-blue-100 text-blue-700'
            : 'bg-muted text-muted-foreground',
        )}
      >
        {isManual ? gt('Manual') : gt('Auto')}
      </span>
    </div>
  );
}

function formatQuantity(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return String(value);
  }

  // Format with max 4 decimal places and truncate trailing zeros
  const formatted = numeric.toFixed(4);
  // Remove trailing zeros and unnecessary decimal point
  return formatted.replace(/\.?0+$/, '');
}

function formatCurrency(value?: string | null, currency = 'USD') {
  if (!value) {
    return '—';
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value;
  }

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
}

interface AggregationSubtotalRowProps {
  group: AggregationGroupInfo;
  matchedPOLineItem?: PurchaseOrderLineItem;
  matchedSOLineItem?: SalesOrderLineItem;
  matchedCharge?: Charge;
  currency: string;
  showMatching: boolean;
  canEditMatches: boolean;
}

/**
 * Subtotal row rendered directly beneath the last member of an aggregation
 * group. Shows the summed invoice quantity/total against the shared matched
 * target (PO line / SO line / charge) so the user can verify the rollup.
 */
function AggregationSubtotalRow({
  group,
  matchedPOLineItem,
  matchedSOLineItem,
  matchedCharge,
  currency,
  showMatching,
  canEditMatches,
}: AggregationSubtotalRowProps) {
  const deleteMutation = useDeleteEntityAssociation();
  const confirmMutation = useConfirmEntityAssociationMatch();
  const invoiceCurrency = currency || 'USD';
  const summedQuantityStr = String(group.summedQuantity);
  const summedTotalStr = String(group.summedTotal);

  const quantityRows: ComparisonRow[] = [
    { label: 'Sum', formattedValue: formatQuantity(summedQuantityStr) },
  ];
  const totalRows: ComparisonRow[] = [
    {
      label: 'Sum',
      formattedValue: formatCurrency(summedTotalStr, invoiceCurrency),
    },
  ];

  let targetLabel = 'Match';
  let targetDescription = '';
  let targetQty: string | number | null | undefined;
  let targetTotal: string | null = null;
  let targetCurrency = invoiceCurrency;

  if (matchedPOLineItem) {
    targetLabel = 'PO';
    targetDescription = matchedPOLineItem.description ?? 'Matched PO line';
    targetQty = matchedPOLineItem.quantity;
    targetTotal =
      matchedPOLineItem.totalPrice == null
        ? null
        : String(matchedPOLineItem.totalPrice);
  } else if (matchedSOLineItem) {
    targetLabel = 'SO';
    targetDescription =
      matchedSOLineItem.productName ||
      matchedSOLineItem.productSku ||
      matchedSOLineItem.customerProductSku ||
      'Matched SO line';
    targetQty = getSOComparisonQuantity(matchedSOLineItem);
    targetTotal = getSOComparisonTotal(matchedSOLineItem);
  } else if (matchedCharge) {
    targetLabel = getMatchLabel(matchedCharge.docType ?? undefined);
    targetDescription = getChargeDisplayName(matchedCharge, 'Matched charge');
    targetQty = null;
    targetTotal =
      matchedCharge.amount != null ? String(matchedCharge.amount) : null;
    targetCurrency = matchedCharge.currencyCode || invoiceCurrency;
  }

  if (targetQty != null) {
    quantityRows.push({
      label: targetLabel,
      formattedValue: formatQuantity(
        typeof targetQty === 'number' ? String(targetQty) : targetQty,
      ),
      isDifferent: valuesDiffer(summedQuantityStr, targetQty),
    });
  }
  if (targetTotal != null) {
    totalRows.push({
      label: targetLabel,
      formattedValue: formatCurrency(targetTotal, targetCurrency),
      isDifferent: valuesDiffer(summedTotalStr, targetTotal),
    });
  }

  const isTentative = isTentativeMatch(group.matchMetadata);
  const hasDifferences =
    (targetQty != null && valuesDiffer(summedQuantityStr, targetQty)) ||
    (targetTotal != null && valuesDiffer(summedTotalStr, targetTotal));

  const chipBorderClass = hasDifferences
    ? 'border-yellow-500 bg-yellow-50'
    : isTentative
      ? 'border-amber-400 bg-amber-50'
      : 'border-green-500 bg-green-50';
  const chipIconClass = hasDifferences
    ? 'text-yellow-600'
    : isTentative
      ? 'text-amber-500'
      : 'text-green-600';
  const ChipIcon = hasDifferences || isTentative ? WarningIcon : CheckIcon;

  const groupTrouble = getMatchTrouble(
    matchedPOLineItem,
    matchedSOLineItem,
    matchedCharge,
  );

  return (
    <TableRow className="bg-muted/40 border-l-4 border-l-blue-400">
      <TableCell className="text-center text-xs font-semibold text-muted-foreground">
        Σ
      </TableCell>
      <TableCell>
        <div className="text-xs font-medium text-muted-foreground">
          {group.commonDescription
            ? `Aggregated "${group.commonDescription}" (${group.memberIds.length} lines)`
            : `Aggregated match (${group.memberIds.length} lines)`}
        </div>
      </TableCell>
      <TableCell>
        <ComparisonCell rows={quantityRows} />
      </TableCell>
      <TableCell />
      <TableCell>
        <ComparisonCell rows={totalRows} />
      </TableCell>
      {showMatching && (
        <TableCell>
          {group.targetEntityId && group.targetEntityType ? (
            <div
              className={cn(
                'flex items-center gap-2 border rounded-md px-2 py-1 max-w-[220px]',
                chipBorderClass,
              )}
            >
              <ChipIcon className={cn('h-4 w-4 shrink-0', chipIconClass)} />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">
                  {targetDescription}
                </div>
                <TroubleDataBadges
                  troubleData={groupTrouble.troubleData}
                  origin={groupTrouble.origin}
                />
                <div className="text-[10px] text-muted-foreground">
                  <SystemEntityInline
                    entityType={group.targetEntityType}
                    entityId={group.targetEntityId}
                    className="text-[10px] h-4 py-0"
                  />
                </div>
              </div>
              {canEditMatches && (
                <AggregationActionsMenu
                  group={group}
                  isTentative={isTentative}
                  deleteMutation={deleteMutation}
                  confirmMutation={confirmMutation}
                />
              )}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">
              <T context="status label for an unmatched invoice line item">
                Not matched
              </T>
            </span>
          )}
        </TableCell>
      )}
      {/* Document column — supporting docs are never aggregated, so the
          subtotal row leaves it blank. */}
      {showMatching && <TableCell />}
      {showMatching && canEditMatches && <TableCell />}
    </TableRow>
  );
}

interface AggregationActionsMenuProps {
  group: AggregationGroupInfo;
  isTentative: boolean;
  deleteMutation: ReturnType<typeof useDeleteEntityAssociation>;
  confirmMutation: ReturnType<typeof useConfirmEntityAssociationMatch>;
}

/**
 * Three-dot menu rendered inside the aggregation subtotal chip. Mirrors the
 * `MatchingCell` per-line affordances ("Confirm match" when tentative,
 * "Remove match"), but applies the action to every member of the group so
 * the rollup stays internally consistent — there's no good UX for half a
 * group being confirmed and the other half not.
 */
function AggregationActionsMenu({
  group,
  isTentative,
  deleteMutation,
  confirmMutation,
}: AggregationActionsMenuProps) {
  const { showToast } = useToast();
  const targetEntityType = group.targetEntityType;
  const targetEntityId = group.targetEntityId;
  if (!targetEntityType || !targetEntityId) return null;

  const isPending = deleteMutation.isPending || confirmMutation.isPending;

  // Fan-out (one mutation per member) instead of a bulk endpoint: the
  // single-association `delete`/`confirmMatch` handlers are idempotent
  // (delete is a no-op when the row is already gone, confirm reports a
  // toast when the association was meanwhile removed), so a partial
  // failure is fully recoverable on retry. We only need to make sure any
  // rejection surfaces to the user instead of becoming an unhandled
  // promise rejection.
  const handleConfirm = async () => {
    try {
      await Promise.all(
        group.memberIds.map((id) =>
          confirmMutation.mutateAsync({
            sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
            sourceEntityId: id,
            targetEntityType,
            targetEntityId,
            associationType: EntityAssociationType.AP_INVOICE_MATCHING,
          }),
        ),
      );
    } catch (error) {
      console.error('Failed to confirm aggregated match', error);
      showToast({
        title: 'Confirm failed',
        message:
          error instanceof Error
            ? error.message
            : 'Some lines in this aggregation could not be confirmed. Please retry.',
        color: 'error',
      });
    }
  };

  const handleRemove = async () => {
    if (
      !window.confirm(
        `Remove the aggregated match for ${group.memberIds.length} invoice lines?`,
      )
    ) {
      return;
    }
    try {
      await Promise.all(
        group.memberIds.map((id) =>
          deleteMutation.mutateAsync({
            sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
            sourceEntityId: id,
            targetEntityType,
            targetEntityId,
            associationType: EntityAssociationType.AP_INVOICE_MATCHING,
          }),
        ),
      );
    } catch (error) {
      console.error('Failed to remove aggregated match', error);
      showToast({
        title: 'Remove failed',
        message:
          error instanceof Error
            ? error.message
            : 'Some lines in this aggregation could not be unmatched. Please retry.',
        color: 'error',
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-5 w-5 p-0 shrink-0"
          onClick={(e) => e.stopPropagation()}
          disabled={isPending}
        >
          <DotsThreeIcon className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {isTentative && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleConfirm();
            }}
          >
            <T context="action to confirm a tentative invoice line item match">
              Confirm match
            </T>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleRemove();
          }}
          className="text-destructive focus:text-destructive"
        >
          <T context="action to remove an invoice line item match">
            Remove match
          </T>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function InvoiceLineItems({
  invoiceId,
  lineItems,
  currency,
  showMatching = true,
  canEditMatches = true,
  supplierId,
}: InvoiceLineItemsProps) {
  const gt = useGT();
  // Check authorization once at component level
  const [canUpdateInvoice] = useCan(Action.Update, Subject.APInvoice);
  const canSplitLines = showMatching && canEditMatches && canUpdateInvoice;
  // Hook for updating line items
  const updateLineItem = useUpdateAPInvoiceLineItem();

  // Fetch invoice associations to get associated documents (POs, SOs, Shipments)
  const invoiceAssociationsQuery = useEntityAssociationsList({
    entityId: invoiceId,
    entityType: SystemEntity.AP_INVOICE,
    associationType: EntityAssociationType.AP_INVOICE_MATCHING,
  });

  // Get associated POs (for line item matching)
  const associatedPOs = useMemo(() => {
    const associations = invoiceAssociationsQuery.data?.associations || [];
    // Filter to only PO-level associations (not line items)
    const poAssociations = associations.filter(
      (assoc) =>
        assoc.entity.entityType === SystemEntity.INTERNAL_PURCHASE_ORDER,
    );
    // Deduplicate by entity ID
    const seen = new Set<string>();
    return poAssociations.filter((assoc) => {
      if (seen.has(assoc.entity.entityId)) {
        return false;
      }
      seen.add(assoc.entity.entityId);
      return true;
    });
  }, [invoiceAssociationsQuery.data?.associations]);

  // Get associated Sales Orders (for charge matching)
  const associatedSOs = useMemo(() => {
    const associations = invoiceAssociationsQuery.data?.associations || [];
    const soAssociations = associations.filter(
      (assoc) => assoc.entity.entityType === SystemEntity.AR_SALES_ORDER,
    );
    const seen = new Set<string>();
    return soAssociations.filter((assoc) => {
      if (seen.has(assoc.entity.entityId)) {
        return false;
      }
      seen.add(assoc.entity.entityId);
      return true;
    });
  }, [invoiceAssociationsQuery.data?.associations]);

  // Get associated Shipments (for charge matching)
  const associatedShipments = useMemo(() => {
    const associations = invoiceAssociationsQuery.data?.associations || [];
    const shipmentAssociations = associations.filter(
      (assoc) => assoc.entity.entityType === SystemEntity.SCM_SHIPMENT,
    );
    const seen = new Set<string>();
    return shipmentAssociations.filter((assoc) => {
      if (seen.has(assoc.entity.entityId)) {
        return false;
      }
      seen.add(assoc.entity.entityId);
      return true;
    });
  }, [invoiceAssociationsQuery.data?.associations]);

  // Fetch all line item associations upfront to avoid N+1 queries
  const lineItemAssociationsMap = useLineItemAssociationsMap(lineItems, {
    withoutProposedSplitLinks: true,
  });

  // Fetch all matched PO line items to compare values
  const matchedPOLineItems = useQueries({
    queries: useMemo(
      () =>
        lineItems.map((lineItem) => {
          const entityId = findAssociatedEntityId(
            lineItemAssociationsMap.get(lineItem.id),
            SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
          );

          return orpc.accountsPayable.purchaseOrders.getLineItem.queryOptions({
            input: { id: entityId },
            enabled: entityId.length > 0,
          });
        }),
      [lineItems, lineItemAssociationsMap],
    ),
    combine: (results) =>
      results.map((result) => result.data?.lineItem ?? null),
  });

  // Fetch all matched charges to display charge category info
  const matchedCharges = useQueries({
    queries: useMemo(
      () =>
        lineItems.map((lineItem) => {
          const entityId = findAssociatedEntityId(
            lineItemAssociationsMap.get(lineItem.id),
            SystemEntity.CHARGE,
          );

          return orpc.finance.charges.getById.queryOptions({
            input: { id: entityId },
            enabled: entityId.length > 0,
          });
        }),
      [lineItems, lineItemAssociationsMap],
    ),
    combine: (results) => results.map((result) => result.data?.charge ?? null),
  });

  // Fetch all associated SOs' data to get their line items for matching display
  // We need this to look up matched SO line items by ID
  const soLineItems = useQueries({
    queries: useMemo(
      () =>
        associatedSOs.map((so) => ({
          ...orpc.accountsReceivable.salesOrders.get.queryOptions({
            input: { id: so.entity.entityId },
            enabled: !!so.entity.entityId,
          }),
          staleTime: 30_000,
        })),
      [associatedSOs],
    ),
    combine: (results) =>
      results.flatMap((result) => result.data?.lineItems ?? []),
  });

  // The combine result is structurally shared on actual content, not
  // dataUpdatedAt. This query shares the same cache key as useSalesOrder() inside
  // the popover's SOLineItemsSection. Opening the popover can trigger a background
  // refetch that updates dataUpdatedAt even when data is unchanged, which would
  // cascade through soLineItemLookup → matchedSOLineItemsMap → columns → table
  // re-render, closing the popover mid-open.
  const soLineItemLookup = useMemo(() => {
    const map = new Map<string, SalesOrderLineItem>();
    for (const li of soLineItems) {
      map.set(li.id, li);
    }
    return map;
  }, [soLineItems]);

  // Build a map of invoice line item ID to matched SO line item
  const matchedSOLineItemsMap = useMemo(() => {
    const map = new Map<string, SalesOrderLineItem>();
    lineItems.forEach((lineItem) => {
      const associations = lineItemAssociationsMap.get(lineItem.id) || [];
      const soMatch = associations.find(
        (assoc) =>
          assoc.entity.entityType === SystemEntity.AR_SALES_ORDER_LINE_ITEM,
      );
      if (soMatch) {
        const soLineItem = soLineItemLookup.get(soMatch.entity.entityId);
        if (soLineItem) {
          map.set(lineItem.id, soLineItem);
        }
      }
    });
    return map;
  }, [lineItems, lineItemAssociationsMap, soLineItemLookup]);

  const matchedPOLineItemsMap = useMemo(() => {
    const map = new Map<string, PurchaseOrderLineItem>();
    lineItems.forEach((lineItem, index) => {
      const item = matchedPOLineItems[index];
      if (item) {
        map.set(lineItem.id, item);
      }
    });
    return map;
  }, [lineItems, matchedPOLineItems]);

  const matchedChargesMap = useMemo(() => {
    const map = new Map<string, Charge>();
    lineItems.forEach((lineItem, index) => {
      const charge = matchedCharges[index];
      if (charge) {
        map.set(lineItem.id, charge);
      }
    });
    return map;
  }, [lineItems, matchedCharges]);

  // Fetch the invoice's supporting documents so we can display receipt-based
  // substantiation matches (e.g. a lumper line backed by a Receipt) in the
  // "Matched To" column. The same query feeds the supporting-documents panel,
  // so this usually hits the cache.
  const { documents: supportingDocs } =
    useAPInvoiceSupportingDocuments(invoiceId);

  const supportingDocLookup = useMemo(() => {
    const map = new Map<string, SupportingDocItem>();
    for (const doc of supportingDocs) {
      map.set(doc.documentId, doc);
    }
    return map;
  }, [supportingDocs]);

  const matchedDocumentsMap = useMemo(() => {
    const map = new Map<string, SupportingDocItem>();
    lineItems.forEach((lineItem) => {
      const associations = lineItemAssociationsMap.get(lineItem.id) || [];
      const docMatch = associations.find(
        (assoc) => assoc.entity.entityType === SystemEntity.CORE_DOCUMENT,
      );
      if (docMatch) {
        const doc = supportingDocLookup.get(docMatch.entity.entityId);
        if (doc) {
          map.set(lineItem.id, doc);
        }
      }
    });
    return map;
  }, [lineItems, lineItemAssociationsMap, supportingDocLookup]);

  // Derive aggregation groups from line item associations. Lines that belong
  // to the same aggregation group share a `metadata.aggregationGroupId` on
  // their entity association. We build a per-line groupId lookup + a group
  // info map used by the sort/render code below.
  const aggregation: AggregationMaps = useMemo(() => {
    const lineIdToGroupId = new Map<string, string>();
    const groups = new Map<string, AggregationGroupInfo>();

    for (const lineItem of lineItems) {
      const assocs = lineItemAssociationsMap.get(lineItem.id);
      const found = readAggregationGroupId(assocs);
      if (!found) continue;

      lineIdToGroupId.set(lineItem.id, found.groupId);

      let group = groups.get(found.groupId);
      if (!group) {
        group = {
          groupId: found.groupId,
          memberIds: [],
          memberIndexById: new Map<string, number>(),
          summedQuantity: 0,
          summedTotal: 0,
          targetEntityType: found.association.entity.entityType,
          targetEntityId: found.association.entity.entityId,
          matchMetadata: found.association.metadata ?? null,
          commonDescription:
            (typeof found.association.metadata?.aggregatedCommonDescription ===
            'string'
              ? found.association.metadata.aggregatedCommonDescription
              : null) ?? null,
        };
        groups.set(found.groupId, group);
      }
      group.memberIds.push(lineItem.id);
      group.summedQuantity += Number(lineItem.quantity) || 0;
      group.summedTotal += Number(lineItem.totalPrice) || 0;
    }

    for (const group of groups.values()) {
      // Sort members by original lineNumber within the group so the order
      // stays stable even if the input array reorders them.
      group.memberIds.sort((a, b) => {
        const la = lineItems.find((l) => l.id === a)?.lineNumber ?? 0;
        const lb = lineItems.find((l) => l.id === b)?.lineNumber ?? 0;
        return la - lb;
      });
      group.memberIds.forEach((id, idx) => group.memberIndexById.set(id, idx));
    }

    return { lineIdToGroupId, groups };
  }, [lineItems, lineItemAssociationsMap]);

  const splitGroups = buildSplitGroups(lineItems);

  // Display order: members of the same aggregation group are placed
  // contiguously, anchored at the group's smallest lineNumber. Non-grouped
  // rows keep their original lineNumber position.
  const sortedLineItems = useMemo(() => {
    if (aggregation.groups.size === 0) return lineItems;

    const groupAnchor = new Map<string, number>();
    for (const [groupId, group] of aggregation.groups) {
      const minLN = Math.min(
        ...group.memberIds.map(
          (id) => lineItems.find((l) => l.id === id)?.lineNumber ?? Infinity,
        ),
      );
      groupAnchor.set(groupId, minLN);
    }
    const anchorOf = (li: InvoiceLineItemDisplay): number => {
      const gid = aggregation.lineIdToGroupId.get(li.id);
      if (gid) return groupAnchor.get(gid) ?? li.lineNumber;
      return li.lineNumber;
    };
    return [...lineItems].sort((a, b) => {
      const diff = anchorOf(a) - anchorOf(b);
      if (diff !== 0) return diff;
      const gidA = aggregation.lineIdToGroupId.get(a.id);
      const gidB = aggregation.lineIdToGroupId.get(b.id);
      // Within a group, preserve memberIds order
      if (gidA && gidA === gidB) {
        const group = aggregation.groups.get(gidA);
        return (
          (group?.memberIndexById.get(a.id) ?? 0) -
          (group?.memberIndexById.get(b.id) ?? 0)
        );
      }
      return a.lineNumber - b.lineNumber;
    });
  }, [lineItems, aggregation]);

  const selection = useLineSelection(
    sortedLineItems,
    (line) => !aggregation.lineIdToGroupId.has(line.id),
  );

  // Define columns
  const columns = useMemo<ColumnDef<InvoiceLineItemDisplay>[]>(() => {
    // Build sets of all matched entity IDs across all invoice line items
    const allMatchedPOLineItemIds = new Set<string>();
    const allMatchedSOLineItemIds = new Set<string>();
    const allMatchedChargeIds = new Set<string>();

    lineItemAssociationsMap.forEach((associations) => {
      associations.forEach((assoc) => {
        if (
          assoc.entity.entityType ===
          SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM
        ) {
          allMatchedPOLineItemIds.add(assoc.entity.entityId);
        } else if (
          assoc.entity.entityType === SystemEntity.AR_SALES_ORDER_LINE_ITEM
        ) {
          allMatchedSOLineItemIds.add(assoc.entity.entityId);
        } else if (assoc.entity.entityType === SystemEntity.CHARGE) {
          allMatchedChargeIds.add(assoc.entity.entityId);
        }
      });
    });

    const baseColumns: ColumnDef<InvoiceLineItemDisplay>[] = [
      {
        id: 'lineNumber',
        accessorKey: 'lineNumber',
        header: () => (
          <div className="text-center">
            <T>#</T>
          </div>
        ),
        cell: (info) => {
          const row = info.row.original;
          const groupId = aggregation.lineIdToGroupId.get(row.id);
          const group = groupId ? aggregation.groups.get(groupId) : undefined;
          const rawIndex = group?.memberIndexById.get(row.id);
          const memberIndex = rawIndex == null ? null : rawIndex + 1;
          return (
            <div className="text-center font-medium text-muted-foreground">
              <div>{row.lineNumber}</div>
              {group && memberIndex != null ? (
                <div className="text-[9px] font-normal text-blue-600">
                  {memberIndex}/{group.memberIds.length}
                </div>
              ) : (
                <SplitMemberMarker
                  splitGroups={splitGroups}
                  lineItemId={row.id}
                />
              )}
            </div>
          );
        },
        size: 48,
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: () => <T>Description</T>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="font-medium">
              {row.description || gt('No description')}
            </div>
          );
        },
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: () => (
          <div className="text-right">
            <T context="quantity abbreviation">Qty</T>
          </div>
        ),
        cell: (info) => {
          const row = info.row.original;
          const isGrouped = aggregation.lineIdToGroupId.has(row.id);
          // For grouped rows we hide per-line vs-target comparison: the
          // comparison lives on the aggregation subtotal row below.
          const matchedPOLineItem = isGrouped
            ? undefined
            : matchedPOLineItemsMap.get(row.id);
          const matchedSOLineItem = isGrouped
            ? undefined
            : matchedSOLineItemsMap.get(row.id);

          return (
            <EditableComparisonCell
              row={row}
              invoiceId={invoiceId}
              field="quantity"
              updateLineItem={updateLineItem}
              canEdit={canEditMatches && canUpdateInvoice}
              matchedPOLineItem={matchedPOLineItem}
              matchedSOLineItem={matchedSOLineItem}
            />
          );
        },
        size: 80,
      },
      {
        id: 'unitPrice',
        accessorKey: 'unitPrice',
        header: () => (
          <div className="text-right">
            <T>Unit Price</T>
          </div>
        ),
        cell: (info) => {
          const row = info.row.original;
          const isGrouped = aggregation.lineIdToGroupId.has(row.id);
          const matchedPOLineItem = isGrouped
            ? undefined
            : matchedPOLineItemsMap.get(row.id);
          const matchedSOLineItem = isGrouped
            ? undefined
            : matchedSOLineItemsMap.get(row.id);

          return (
            <EditableComparisonCell
              row={row}
              invoiceId={invoiceId}
              field="unitPrice"
              updateLineItem={updateLineItem}
              canEdit={canEditMatches && canUpdateInvoice}
              matchedPOLineItem={matchedPOLineItem}
              matchedSOLineItem={matchedSOLineItem}
              currency={currency}
            />
          );
        },
        size: 112,
      },
      {
        id: 'totalPrice',
        accessorKey: 'totalPrice',
        header: () => (
          <div className="text-right">
            <T>Total</T>
          </div>
        ),
        cell: (info) => {
          const row = info.row.original;
          const isGrouped = aggregation.lineIdToGroupId.has(row.id);
          // Grouped rows skip the per-line comparison — the summed comparison
          // is rendered on the aggregation subtotal row below.
          const matchedPOLineItem = isGrouped
            ? undefined
            : matchedPOLineItemsMap.get(row.id);
          const matchedSOLineItem = isGrouped
            ? undefined
            : matchedSOLineItemsMap.get(row.id);
          const matchedCharge = isGrouped
            ? undefined
            : matchedChargesMap.get(row.id);

          const splitParts = confirmedSplitParts(
            lineItemAssociationsMap.get(row.id),
          );
          const rows: ComparisonRow[] = [
            {
              label: 'Inv',
              formattedValue: formatCurrency(row.totalPrice, currency),
            },
          ];
          if (matchedPOLineItem) {
            const poTotalPrice =
              matchedPOLineItem.totalPrice === null ||
              matchedPOLineItem.totalPrice === undefined
                ? null
                : String(matchedPOLineItem.totalPrice);
            rows.push({
              label: 'PO',
              formattedValue: formatCurrency(poTotalPrice, currency),
              isDifferent: valuesDiffer(
                row.totalPrice,
                matchedPOLineItem.totalPrice,
              ),
            });
          } else if (matchedSOLineItem) {
            const soTotal = getSOComparisonTotal(matchedSOLineItem);
            rows.push({
              label: 'SO',
              formattedValue: formatCurrency(soTotal, currency),
              isDifferent: valuesDiffer(row.totalPrice, soTotal),
            });
          } else {
            const chargeRow = chargeComparisonRow({
              splitParts,
              matchedCharge,
              lineTotal: row.totalPrice,
              currency,
            });
            if (chargeRow) rows.push(chargeRow);
          }

          return <ComparisonCell rows={rows} />;
        },
        size: 112,
      },
    ];

    if (showMatching) {
      baseColumns.push({
        id: 'matching',
        header: () => <T>Matched To</T>,
        cell: (info) => {
          const row = info.row.original;
          const groupId = aggregation.lineIdToGroupId.get(row.id);
          const group = groupId ? aggregation.groups.get(groupId) : undefined;
          const rawIndex = group?.memberIndexById.get(row.id);
          const memberIndex = rawIndex == null ? null : rawIndex + 1;
          const aggregationInfo =
            group && memberIndex != null
              ? { memberIndex, memberCount: group.memberIds.length }
              : undefined;

          return (
            <MatchCellOrSplitSuggestion
              line={row}
              associations={lineItemAssociationsMap.get(row.id)}
              money={(amount) => formatCurrency(amount, currency)}
            >
              <MatchingCell
                lineItemId={row.id}
                lineItem={row}
                canEditMatches={canEditMatches}
                associatedPOs={associatedPOs}
                associatedSOs={associatedSOs}
                associatedShipments={associatedShipments}
                lineItemAssociations={lineItemAssociationsMap.get(row.id)}
                allMatchedPOLineItemIds={allMatchedPOLineItemIds}
                allMatchedSOLineItemIds={allMatchedSOLineItemIds}
                allMatchedChargeIds={allMatchedChargeIds}
                matchedPOLineItem={matchedPOLineItemsMap.get(row.id)}
                matchedSOLineItem={matchedSOLineItemsMap.get(row.id)}
                matchedCharge={matchedChargesMap.get(row.id)}
                currency={currency}
                aggregationInfo={aggregationInfo}
              />
            </MatchCellOrSplitSuggestion>
          );
        },
        size: 150,
      });

      baseColumns.push({
        id: 'document-match',
        header: () => <T>Document</T>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <DocumentMatchCell
              lineItemId={row.id}
              canEditMatches={canEditMatches && canUpdateInvoice}
              lineItemAssociations={lineItemAssociationsMap.get(row.id)}
              matchedDocument={matchedDocumentsMap.get(row.id)}
              currency={currency}
            />
          );
        },
        size: 150,
      });
    }

    if (canSplitLines) {
      baseColumns.push({
        id: 'actions',
        header: () => null,
        cell: (info) => {
          const row = info.row.original;
          if (aggregation.lineIdToGroupId.has(row.id)) return null;
          return (
            <div className="flex items-center gap-1">
              <LineSelectCheckbox
                lineNumber={row.lineNumber}
                checked={selection.selectedIds.has(row.id)}
                onCheckedChange={(checked) => selection.toggle(row.id, checked)}
              />
              <SplitLineItemButton
                invoiceId={invoiceId}
                lineItem={row}
                currency={currency || 'USD'}
              />
            </div>
          );
        },
        size: 72,
      });
    }

    return baseColumns;
  }, [
    gt,
    currency,
    showMatching,
    canEditMatches,
    canUpdateInvoice,
    associatedPOs,
    associatedSOs,
    associatedShipments,
    lineItemAssociationsMap,
    matchedPOLineItemsMap,
    matchedSOLineItemsMap,
    matchedChargesMap,
    matchedDocumentsMap,
    updateLineItem,
    invoiceId,
    aggregation,
    canSplitLines,
    splitGroups,
    selection,
  ]);

  const isLineMatched = (lineItemId: string) =>
    (lineItemAssociationsMap.get(lineItemId) ?? []).some(
      (association) =>
        association.entity.entityType !== SystemEntity.CORE_DOCUMENT &&
        !isTentativeMatch(association.metadata),
    );

  const table = useTable({
    features: appTableFeatures,
    data: sortedLineItems,
    columns,
    getRowId: (row) => row.id,
  });

  // Calculate totals for the footer row
  const totals = useMemo(() => {
    let totalQuantity = 0;
    let totalPrice = 0;
    lineItems.forEach((item) => {
      totalQuantity += Number(item.quantity) || 0;
      totalPrice += Number(item.totalPrice) || 0;
    });
    return { totalQuantity, totalPrice };
  }, [lineItems]);

  return (
    <div className="space-y-2">
      {canSplitLines && (
        <AggregateLinesBar
          invoiceId={invoiceId}
          supplierId={supplierId}
          allLineItems={lineItems}
          selectedLines={selection.selectedLines}
          currency={currency || 'USD'}
          onClear={selection.clear}
        />
      )}
      <div className="border rounded-md mx-3 overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-muted/50 backdrop-blur">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{
                        width:
                          header.getSize() !== 150
                            ? `${header.getSize()}px`
                            : undefined,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => {
                const groupId = aggregation.lineIdToGroupId.get(row.id);
                const group = groupId
                  ? aggregation.groups.get(groupId)
                  : undefined;
                const isLastInGroup =
                  group &&
                  group.memberIds[group.memberIds.length - 1] === row.id;
                /* eslint-disable i18next/no-literal-string -- CSS class names, not user-visible text */
                const memberRowClass = group
                  ? 'border-l-4 border-l-blue-400 bg-blue-50/30'
                  : splitMemberRowClass(splitGroups, row.id);
                /* eslint-enable i18next/no-literal-string */

                return (
                  <React.Fragment key={row.id}>
                    <TableRow className={memberRowClass}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {isLastInGroup && group && (
                      <AggregationSubtotalRow
                        group={group}
                        matchedPOLineItem={matchedPOLineItemsMap.get(
                          group.memberIds[0],
                        )}
                        matchedSOLineItem={matchedSOLineItemsMap.get(
                          group.memberIds[0],
                        )}
                        matchedCharge={matchedChargesMap.get(
                          group.memberIds[0],
                        )}
                        currency={currency || 'USD'}
                        showMatching={showMatching}
                        canEditMatches={canEditMatches && canUpdateInvoice}
                      />
                    )}
                    <SplitGroupSubtotalRow
                      invoiceId={invoiceId}
                      splitGroups={splitGroups}
                      lineItemId={row.id}
                      currency={currency || 'USD'}
                      isLineMatched={isLineMatched}
                      showMatching={showMatching}
                      showActionsColumn={canSplitLines}
                    />
                  </React.Fragment>
                );
              })}
            </TableBody>
            <TableFooter className="sticky bottom-0 bg-muted/95 backdrop-blur">
              <TableRow>
                <TableCell />
                <TableCell className="font-semibold">
                  <T>Total</T>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell className="text-right font-semibold">
                  {formatCurrency(String(totals.totalPrice), currency)}
                </TableCell>
                {showMatching && (
                  <>
                    <TableCell />
                    <TableCell />
                  </>
                )}
                {canSplitLines && <TableCell />}
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </div>
    </div>
  );
}
